/**
 * Centralized error handling utility for API calls
 * Provides consistent error tracking, reporting, and user notifications
 */
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import axios from "axios";

import protectedAxios from "./axios";
import { API_URL } from "./constants";
import {
  ENTITY_NAME,
  ENTITY_ACTION,
  CONTENT_TYPE_TO_ENTITY_ACTION,
  SERVICE_NAME_TO_ENTITY_NAME,
  SPAM_SEVERITY,
  ERROR_SEVERITY,
  ENDPOINT_CRITICALITY,
  SPAM_TOAST_SETTINGS,
  SPAM_LOG_MESSAGES,
  DEFAULT_SPAM_CHECK_RESPONSE,
} from "./constants/spamDetection";
import { setWithExpiry, getWithExpiry } from "./utils";
import { BROWSER_FINGER_PRINT_KEY } from "./constants";

// IP caching with TTL
const IP_CACHE_KEY = "spam_check_client_ip";
const IP_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const IP_REQUEST_TIMEOUT = 5000; // 5 seconds

// UserDevice ID caching
const USER_DEVICE_CACHE_KEY = "spam_check_user_device_id";
const USER_DEVICE_CACHE_TTL = 30 * 60 * 1000; // 30 minutes (longer since UserDevice IDs don't change often)

/**
 * Determine if an error should be shown to the user
 *
 * @param {string} endpoint - API endpoint where the error occurred
 * @param {Error} error - The error object
 * @returns {boolean} - Whether to show the error to the user
 */
export const shouldShowErrorToUser = (endpoint, error) => {
  // Get endpoint criticality
  const criticality = ENDPOINT_CRITICALITY[endpoint] || ERROR_SEVERITY.MEDIUM;

  // For low severity errors, never show to user
  if (criticality === ERROR_SEVERITY.LOW) {
    return false;
  }

  // For network errors on non-critical endpoints, don't show to user
  if (criticality !== ERROR_SEVERITY.HIGH && axios.isCancel(error)) {
    return false;
  }

  // For rate limiting errors, only show if they persist
  if (error.response && error.response.status === 429) {
    return false; // Handle rate limiting errors silently
  }

  // By default, show errors for medium and high criticality endpoints
  return criticality !== ERROR_SEVERITY.LOW;
};

// Track rate limit errors to avoid showing too many notifications
const rateLimitErrorCounts = {};
const RATE_LIMIT_ERROR_THRESHOLD = 3;

/**
 * Get cached IP address using existing utility
 * @returns {string|null} The cached IP address
 */
const getCachedIP = () => {
  return getWithExpiry(IP_CACHE_KEY);
};

/**
 * Cache IP address using existing utility
 * @param {string} ip - IP address to cache
 */
const cacheIP = (ip) => {
  setWithExpiry(IP_CACHE_KEY, ip, IP_CACHE_TTL);
};

/**
 * Get client IP address with caching and fallback
 * @returns {Promise<string|null>} The client's IP address
 */
const getClientIP = async () => {
  // Check cache first
  const cachedIP = getCachedIP();
  if (cachedIP) {
    return cachedIP;
  }

  try {
    // Use our own backend endpoint with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), IP_REQUEST_TIMEOUT);

    const response = await fetch(`${API_URL}/v1/geoip/client-ip`, {
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    if (data.ip && data.ip !== "unknown") {
      cacheIP(data.ip);
      return data.ip;
    }
  } catch (error) {
    if (error.name === "AbortError") {
      console.warn("IP request timed out, trying fallback");
    } else {
      console.warn("Failed to get client IP from backend:", error);
    }

    // Fallback to external service if backend fails
    try {
      const fallbackController = new AbortController();
      const fallbackTimeoutId = setTimeout(
        () => fallbackController.abort(),
        IP_REQUEST_TIMEOUT
      );

      const fallbackResponse = await fetch(
        "https://api.ipify.org?format=json",
        {
          signal: fallbackController.signal,
        }
      );
      clearTimeout(fallbackTimeoutId);

      if (!fallbackResponse.ok) {
        throw new Error(`Fallback HTTP ${fallbackResponse.status}`);
      }

      const fallbackData = await fallbackResponse.json();
      if (fallbackData.ip) {
        cacheIP(fallbackData.ip);
        return fallbackData.ip;
      }
    } catch (fallbackError) {
      console.warn(
        "Failed to get client IP from fallback service:",
        fallbackError
      );
    }
  }

  return null;
};

/**
 * Get device fingerprint using existing system
 * @returns {string|null} Device fingerprint
 */
const getDeviceFingerprint = () => {
  try {
    // Use existing fingerprinting system from the codebase
    // Following the same pattern as ProfileVisitsContainer.jsx
    let fingerprint = window.localStorage.getItem(BROWSER_FINGER_PRINT_KEY);
    if (fingerprint) {
      fingerprint = JSON.parse(fingerprint)?.fingerPrint;
      return fingerprint;
    }
    return null;
  } catch (error) {
    console.warn("Failed to get device fingerprint:", error);
    return null;
  }
};

/**
 * Get UserDevice ObjectId by fingerprint
 * @param {string} fingerprint - Device fingerprint
 * @returns {Promise<string|null>} UserDevice ObjectId
 */
const getUserDeviceId = async (fingerprint) => {
  if (!fingerprint) return null;

  // Check cache first
  const cachedUserDeviceId = getWithExpiry(USER_DEVICE_CACHE_KEY);
  if (cachedUserDeviceId) {
    return cachedUserDeviceId;
  }

  try {
    const response = await protectedAxios.get(
      `${API_URL}/v1/activity-logger/user-device?fingerPrint=${fingerprint}`
    );
    const userDeviceId = response.data?.userDeviceId || null;

    // Cache the result only if UserDevice exists
    if (userDeviceId) {
      setWithExpiry(USER_DEVICE_CACHE_KEY, userDeviceId, USER_DEVICE_CACHE_TTL);
    }

    return userDeviceId;
  } catch (error) {
    console.warn("Failed to get UserDevice ID:", error);
    return null;
  }
};

/**
 * Central error handling function for API errors
 *
 * @param {string} endpoint - API endpoint where the error occurred
 * @param {Error} error - The error object
 * @param {Function} dispatch - Redux dispatch function (optional)
 * @param {Object} options - Additional options
 * @param {string} options.actionType - Redux action type for error (if using dispatch)
 * @param {Object} options.actionPayload - Additional data for the action
 * @returns {Error} - The original error for further handling if needed
 */
export const handleApiError = (
  endpoint,
  error,
  dispatch = null,
  options = {}
) => {
  // Always log to console for debugging
  console.error(`API error in ${endpoint}:`, error);

  // Track rate limit errors
  if (error.response && error.response.status === 429) {
    rateLimitErrorCounts[endpoint] = (rateLimitErrorCounts[endpoint] || 0) + 1;
  }

  // Track in Sentry with appropriate tags
  Sentry.withScope((scope) => {
    scope.setTag("endpoint", endpoint);
    scope.setTag(
      "criticality",
      ENDPOINT_CRITICALITY[endpoint] || ERROR_SEVERITY.MEDIUM
    );

    // Add additional context based on error type
    if (error.response) {
      scope.setExtra("status", error.response.status);
      scope.setExtra("data", error.response.data);
    }

    Sentry.captureException(error);
  });

  // Determine if we should show error to user
  if (shouldShowErrorToUser(endpoint, error)) {
    // For rate limit errors, only show after multiple occurrences
    if (error.response && error.response.status === 429) {
      if (rateLimitErrorCounts[endpoint] >= RATE_LIMIT_ERROR_THRESHOLD) {
        toast.error(
          "We're experiencing high traffic. Please try again in a moment."
        );
        // Reset counter after showing
        rateLimitErrorCounts[endpoint] = 0;
      }
    } else {
      // Show toast notification for other errors
      toast.error(
        error?.response?.data?.message ||
          "We're having trouble connecting to the server. Please try again later."
      );
    }
  }

  // If redux dispatch provided, dispatch appropriate error action
  if (dispatch && options.actionType) {
    dispatch({
      type: options.actionType,
      payload: {
        ...(options.actionPayload || {}),
        error: error?.response?.data?.message || "An error occurred",
      },
    });
  }

  return error; // Return the error for further handling if needed
};

/**
 * Handle spam-related errors specifically
 *
 * @param {Error} error - The error object from API response
 * @param {Function} toast - Toast notification function
 * @returns {Object|null} - Spam details if it's a spam error, null otherwise
 */
export const handleSpamError = (error, toast = null) => {
  // Handle both error structures: error.response.data and error.data
  const responseData = error.response?.data || error.data;

  if (responseData && responseData.spamDetails) {
    const { spamDetails } = responseData;
    const { severity } = spamDetails;

    const errorMessage =
      "Content doesn't follow our community guidelines. Please review and try again.";

    // Show toast notification if available
    if (toast) {
      toast.error(errorMessage, SPAM_TOAST_SETTINGS);
    }

    // Log spam detection details for debugging (optional)
    console.log("Spam detection triggered:", {
      severity: severity || "unknown",
    });

    // Return spam details for further handling
    return {
      severity: severity || "unknown",
      errorMessage,
    };
  }

  return null;
};

/**
 * Generic spam check function
 *
 * @param {string} content - The content to check for spam
 * @param {string} serviceName - The service name (e.g., "quest_creation", "quest_updation", "message_send")
 * @param {string} contentType - The content type (e.g., "QUEST_DESCRIPTION", "MESSAGE")
 * @param {Object} metadata - Additional metadata for the spam check
 * @param {Function} axiosInstance - The axios instance to use for the request
 * @returns {Promise<Object>} - The spam check result
 *
 * @example
 * // For quest creation
 * const result = await challenge(
 *   questText,
 *   "quest_creation",
 *   "QUEST_DESCRIPTION",
 *   { isAnonymous: false, intentMeta: { intent: "general" } }
 * );
 *
 * // For quest updates
 * const result = await challenge(
 *   updatedQuestText,
 *   "quest_updation",
 *   "QUEST_DESCRIPTION",
 *   { questId: "123", updateType: "questText" }
 * );
 *
 * // For messages
 * const result = await challenge(
 *   messageText,
 *   "message_send",
 *   "MESSAGE_DESCRIPTION",
 *   { conversationId: "456" }
 * );
 */
export const challenge = async (
  content,
  serviceName,
  contentType = "QUEST_DESCRIPTION",
  metadata = {},
  axiosInstance = null,
  isAnonymous = false
) => {
  try {
    // Get entity name from service name mapping
    let entityName = SERVICE_NAME_TO_ENTITY_NAME[serviceName];
    if (!entityName) {
      console.warn(
        SPAM_LOG_MESSAGES.UNKNOWN_SERVICE.replace("{serviceName}", serviceName)
      );
      entityName = ENTITY_NAME.QUEST;
    }

    // Get entity action from content type mapping
    let entityAction = CONTENT_TYPE_TO_ENTITY_ACTION[contentType];
    if (!entityAction) {
      console.warn(
        SPAM_LOG_MESSAGES.UNKNOWN_CONTENT_TYPE.replace(
          "{contentType}",
          contentType
        )
      );
      entityAction = ENTITY_ACTION.QUEST_QUESTION;
    }

    // Get client IP address
    const clientIP = await getClientIP();

    // Get device fingerprint and UserDevice ID
    const deviceFingerprint = getDeviceFingerprint();
    const userDeviceId = await getUserDeviceId(deviceFingerprint);

    // Extract entityId, entityType, and parentId from metadata
    const { entityId, entityType, parentId, ...otherMetadata } = metadata;

    // Log spam check attempt for debugging (without sensitive content)
    if (!import.meta.env.PROD) {
      console.log("Spam check initiated:", {
        serviceName,
        contentType,
        hasContent: !!content,
        contentLength: content?.length || 0,
        hasIP: !!clientIP,
        hasFingerprint: !!deviceFingerprint,
        hasUserDeviceId: !!userDeviceId,
        hasEntityId: !!entityId,
        hasEntityType: !!entityType,
        hasParentId: !!parentId,
        timestamp: new Date().toISOString(),
      });
    }

    const httpClient = axiosInstance || (isAnonymous ? axios : protectedAxios);
    const response = await httpClient.post(
      `${API_URL}/v1/ugc/challenge`,
      {
        content,
        entity: {
          name: entityName,
          action: entityAction,
          entityId,
          entityType,
          parentId,
        },
        user: {
          gameplayerId: null, // This will be set by the backend from JWT token
          ip: clientIP,
          device: userDeviceId, // Only use UserDevice ID, no fallback to fingerprint
        },
        metadata: otherMetadata, // Exclude entityId, entityType, parentId from metadata
      }
    );

    // Use single spam error message
    const result = response.data;
    if (result.isSpam) {
      result.message =
        "Content doesn't follow our community guidelines. Please review and try again.";
    }

    return result;
  } catch (error) {
    // Capture error in Sentry with detailed context
    Sentry.withScope((scope) => {
      scope.setTag("service", "challenge");
      scope.setTag("serviceName", serviceName);
      scope.setTag("contentType", contentType);
      scope.setExtra("error_details", {
        serviceName,
        contentType,
        contentLength: content?.length || 0,
        hasMetadata: !!metadata,
        errorType: error.constructor.name,
        errorMessage: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
      });
      scope.setLevel("warning"); // Use warning level for spam check errors
      Sentry.captureException(error);
    });

    // Return a safe default response
    return DEFAULT_SPAM_CHECK_RESPONSE;
  }
};

// Export a helper for specific endpoints
export const createEndpointErrorHandler = (endpoint) => {
  return (error, dispatch = null, options = {}) => {
    return handleApiError(endpoint, error, dispatch, options);
  };
};
